import produce from "immer";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { ConnectionBaseFragment } from "@/apollo/types";
import { Heading } from "@/components/elements/Typography";
import ConnectionSettings from "@/components/modules/ConnectionSettings";
import { TaskAction } from "@/components/modules/TaskActionBar";
import { useStepsContext } from "@/components/modules/stepper";
import useConnectionSettings from "@/hooks/useConnectionSettings";
import {
  NextButton,
  PrevButton,
} from "@/widgets/syncs/shared/ui/StepNavButton";

import {
  createInitialState,
  useReverseETLStateContext,
} from "../../state/StepStateContext";

export function ConnectionSettingsStep() {
  const { state } = useReverseETLStateContext();
  const connection = state.destination.value?.connection;
  if (!connection) {
    return null;
  }
  return <ConnectionSettingsStepContent connection={connection} />;
}

type FormValues = {
  settings: Record<string, any>;
  __isValidHack__: boolean;
};

function ConnectionSettingsStepContent(props: {
  connection: ConnectionBaseFragment;
}) {
  const stepsContext = useStepsContext();
  const { state, setState } = useReverseETLStateContext();

  const formMethods = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      settings: state.connectionSettings.value?.settings || {},
      __isValidHack__: true,
    },
  });

  const { isValid } = formMethods.formState;

  const connectionSettingValues = formMethods.watch("settings");

  const connectionSettings = useConnectionSettings(
    props.connection.id,
    "Activate",
  );

  return (
    <FormProvider {...formMethods}>
      <div className="mb-6">
        <Heading className="mb-1">Target Details</Heading>
        <p className="text-sm text-muted-foreground">
          Specify the exact location and settings within your selected
          destination. These settings determine where and how your data will be
          delivered.
        </p>
      </div>
      <form
        id="connection-settings-form"
        onSubmit={formMethods.handleSubmit((data) => {
          setState(
            produce((state) => {
              state.connectionSettings = {
                value: {
                  settings: data.settings,
                },
                isValid: true,
                isDirty: false,
              };
            }),
          );
          stepsContext.onNextStep();
        })}
        className="w-full"
      >
        <input
          type={"hidden"}
          {...formMethods.register("__isValidHack__", {
            validate: (value) => {
              return value || "Invalid";
            },
          })}
        />
        <div className="flex flex-col gap-8">
          <Controller<FormValues, "settings">
            name="settings"
            render={({ field }) => (
              <ConnectionSettings
                connectionSettings={connectionSettings}
                onChange={(settings) => {
                  field.onChange({
                    ...field.value,
                    ...settings,
                  });
                }}
                onChangeValid={(isValid) => {
                  if (formMethods.getValues("__isValidHack__") !== isValid) {
                    formMethods.setValue("__isValidHack__", isValid, {
                      shouldValidate: true,
                    });
                  }
                }}
                initialValues={{
                  ...connectionSettingValues,
                  connectionId: props.connection.id,
                }}
              />
            )}
          />
        </div>
        <TaskAction>
          <div className="flex flex-row-reverse gap-4">
            <NextButton
              type="submit"
              form="connection-settings-form"
              isDisabled={!isValid}
            />
            <PrevButton
              onClick={() => {
                setState(createInitialState());
              }}
            />
          </div>
        </TaskAction>
      </form>
    </FormProvider>
  );
}
